'use client';

import { useRouter } from 'next/navigation';
import { useMemo, useState, useTransition } from 'react';
import toast from 'react-hot-toast';

import { useReportListing, useSendListingToFriend } from '@/slw/entities/buyer/listing/listing.mutation';
import { useAddFavorite, useRemoveFavorite } from '@/slw/entities/favorite/favorite.mutation';
import { useFavouriteListings } from '@/slw/entities/favorite/favorite.query';
import { EmailVehicleFormValues, ReportListingFormValues } from '@/slw/entities/listing/listing.schema';
import { checkExteriorConditionEmpty, checkVehicleStatusEmpty, ListingPayload } from '@/slw/entities/seller/listing';
import { useAuthStore } from '@/slw/shared/stores/auth.store';
import { BannerSelling } from '@/slw/widgets/common/banner-selling.ui';
import { Breadcrumb } from '@/slw/widgets/common/breadcrumb.ui';
import { AccordionSection } from '@/slw/widgets/listing/accordion-section';
import { EmailVehicleModal } from '@/slw/widgets/listing/email-vehicle.modal';
import { ListingExterior } from '@/slw/widgets/listing/listing-exterior';
import { ListingFeatures } from '@/slw/widgets/listing/listing-features';
import { ListingGallery } from '@/slw/widgets/listing/listing-gallery';
import { ListingHeaderActions } from '@/slw/widgets/listing/listing-header-actions';
import { ListingOverview } from '@/slw/widgets/listing/listing-overview';
import { ListingSidebar } from '@/slw/widgets/listing/listing-sidebar';
import { ListingStatus } from '@/slw/widgets/listing/listing-status';
import { ListingVideos } from '@/slw/widgets/listing/listing-videos';
import { ReportListingModal } from '@/slw/widgets/listing/report-listing.modal';
import { createChatWithListing } from '@/slw/shared/lib/firebase/utils';
import { User } from '@/slw/entities/chat';
import { useChatStore } from '@/slw/shared/stores';
import { useChatStore as useChatStoreFirebase } from '@/slw/shared/lib/firebase/chat-store';

export function ListingDetailPage({ data }: { data: ListingPayload }) {
  const [showReport, setShowReport] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const { user } = useAuthStore();
  const [isPending, startTransition] = useTransition();
  const router = useRouter();
  const { openChat } = useChatStore();
  const { changeChat } = useChatStoreFirebase();

  // Favorite system hooks
  const { data: favouriteListings } = useFavouriteListings(user?.id || '', {
    PageIndex: 0,
    PageSize: 9999999,
  });
  const addFavourite = useAddFavorite();
  const removeFavourite = useRemoveFavorite();

  // Determine if this listing is a favorite
  const favorite =
    Array.isArray(favouriteListings?.data) && favouriteListings.data.find((fav) => fav.carId === data.id);

  // Handle favorite toggle
  const handleFavorite = () => {
    startTransition(() => {
      if (!user?.id || !data.id) return;

      if (favorite) {
        removeFavourite.mutate({ id: favorite.id, carId: data.id || '', userId: user.id });
      } else {
        addFavourite.mutate({ carId: data.id || '', userId: user.id });
      }
    });
  };

  const reportMutation = useReportListing();

  const emailVehicleMutation = useSendListingToFriend();

  const handleSubmitReportListing = async (values: ReportListingFormValues) => {
    try {
      await reportMutation.mutateAsync({
        ...values,
        carId: data.id || '',
        listingId: data.listingId || '',
        title: data.title || '',
        subTitle: data.subTitle || '',
      });
      setShowReport(false);
    } catch (error) {
      toast.error((error as Error).message || 'Failed to sens email');
    }
  };

  const handleSendEmailVehicle = async (values: EmailVehicleFormValues) => {
    try {
      await emailVehicleMutation.mutateAsync({
        ...values,
        carUrl: data.url,
        carId: data.id || '',
        listingId: data.listingId || '',
        title: data.title || '',
        subTitle: data.subTitle || '',
        url: window.location.href,
      });
      setShowEmailModal(false);
    } catch {}
  };

  const handleReport = () => {
    setShowReport(true);
  };

  const handleRedirectEmail = () => {
    if (user?.id) {
      setShowEmailModal(true);

      return;
    }

    const link = 'https://gmail.com/';

    setTimeout(() => {
      window.open(link);
    }, 1500);
  };

  // Twitter handler: open Twitter share
  const handleRedirectTwitter = () => {
    const link = 'https://twitter.com';

    setTimeout(() => {
      window.open(link);
    }, 1500);
  };

  // Messenger handler: open Messenger share
  const handleRedirectMessenger = () => {
    const link = 'https://www.facebook.com';

    setTimeout(() => {
      window.open(link);
    }, 1500);
  };

  // Conversation handler: navigate to chat page
  const handleStartMessageSeller = () => {
    startTransition(async () => {
      if (data.id) {
        const response = await createChatWithListing({
          listingData: {
            url: data.url || '',
            title: data.title || '',
            imageUrl: data.mainImageUrl || '',
            listingId: data.listingId || '',
            price: data.price || null,
          },
          sender: {
            id: user.id || '',
            username: `${user.firstName} ${user.lastName}`,
          } as User,
          receiver: {
            id: data.userId || '',
            username: data.userName,
          } as User,
        });

        if (response.success && response.chatId && response.receiverUser) {
          changeChat(response.chatId, response.receiverUser);
          openChat();
        }
      }
    });
  };

  // Similar vehicles handler: navigate to similar listings
  const handleRedirectSimilarVehicles = () => {
    if (data?.id) {
      router.push(`/search-results?make=${data.make}&model=${data.model}`);
    }
  };

  const disableMessageSellerBtn = useMemo(() => !user?.id || user.id === data.userId, [data.userId, user.id]);

  return (
    <div className='container mx-auto px-2 md:px-8'>
      {/* Breadcrumbs */}
      <Breadcrumb data={[{ name: 'Search results', href: '/search-results' }, { name: 'Listing details' }]} />

      {/* Title & Favorite & Report */}
      <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8'>
        <h1 className='text-[1.5rem] md:text-[2.5rem] font-bold leading-[120%]'>{data.title}</h1>
        <ListingHeaderActions
          isLoading={isPending || !user?.id}
          isFavorite={!!favorite}
          onFavorite={handleFavorite}
          onReport={handleReport}
        />
      </div>
      {/* Main layout */}
      <div className='flex flex-col lg:flex-row gap-6'>
        {/* Left column: Main content */}
        <div className='flex-1 flex flex-col gap-12'>
          {/* Gallery */}
          <ListingGallery
            images={data.carImages || []}
            mainImageUrl={data.mainImageUrl ?? ''}
            mainImageId={data.mainImageId ?? ''}
          />
          
          {/* Videos */}
        {(() => {
          // Use only individual video URL fields
          const allVideoUrls = [
            data.videoUrl1,           // Field 1
            data.videoUrl2,           // Field 2
            data.videoUrl3            // Field 3
          ].filter((url): url is string => url != null && url.trim() !== ''); // Filter out empty URLs
          
          return allVideoUrls.length > 0 ? (
            <ListingVideos videoUrls={allVideoUrls} />
          ) : null;
        })()}
          <div className='flex flex-col py-8 gap-12 border border-[#EBE9E5] rounded-md'>
            {/* Vehicle Overview */}
            <ListingOverview listing={data} />
            {/* Features */}
            <ListingFeatures listing={data} />
            {/* Status */}
            {!checkVehicleStatusEmpty(data) ? (
              <AccordionSection id='status' title='Vehicle Status'>
                <ListingStatus listing={data} />
              </AccordionSection>
            ) : null}
            {/* Exterior & Interior */}
            {!checkExteriorConditionEmpty(data) ? (
              <AccordionSection id='exterior' title='Vehicle Exterior & Interior'>
                <ListingExterior listing={data} />
              </AccordionSection>
            ) : null}
          </div>
        </div>
        {/* Right column: Sidebar */}
        <ListingSidebar
          data={data}
          disableMessageSellerBtn={disableMessageSellerBtn}
          isLoading={!user?.id}
          onRedirectEmail={handleRedirectEmail}
          onRedirectTwitter={handleRedirectTwitter}
          onRedirectMessenger={handleRedirectMessenger}
          onRedirectConversation={handleStartMessageSeller}
          onRedirectSimilarVehicles={handleRedirectSimilarVehicles}
        />
      </div>
      {/* Banner CTA */}
      <div className='mt-12'>
        <BannerSelling />
      </div>
      {/* Report Modal (placeholder) */}
      {user?.id && showReport && (
        <ReportListingModal
          onSubmitReportListing={handleSubmitReportListing}
          open={showReport}
          onOpenChange={setShowReport}
        />
      )}
      {user?.id && (
        <EmailVehicleModal
          onSendEmail={handleSendEmailVehicle}
          open={showEmailModal}
          onOpenChange={setShowEmailModal}
        />
      )}
    </div>
  );
}
